import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/**
 * One labelled form control. It wires the single child it wraps: `id` (the label's `htmlFor`),
 * `aria-invalid` while there is an error, `aria-describedby` to the error, the locked note and the
 * help (in that order), `required`, and `disabled` when the field is locked by a permission. The
 * copy arrives translated; `locked` is the translated action label ("Edit carrier terms"), rendered
 * as `permissions.lockedField` under the field (design 3: fields disable, whole actions hide).
 * The help reads at 11.5 px (UX audit S9).
 */

type ControlProps = {
  id?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
  'aria-describedby'?: string;
  disabled?: boolean;
  required?: boolean;
};

export interface FormFieldProps {
  id: string;
  label: string;
  children: React.ReactElement<ControlProps>;
  error?: string | null;
  help?: string | null;
  /** The translated label of the permission that locks this field; the control is disabled. */
  locked?: string | null;
  required?: boolean;
  className?: string;
}

export function FormField({ id, label, children, error, help, locked, required, className }: FormFieldProps) {
  const { t } = useTranslation();
  const errorId = `${id}-error`;
  const lockedId = `${id}-locked`;
  const helpId = `${id}-help`;
  const describedBy = [error ? errorId : null, locked ? lockedId : null, help ? helpId : null]
    .filter(Boolean)
    .join(' ');

  const control = React.cloneElement(children, {
    id,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy || undefined,
    disabled: Boolean(locked) || children.props.disabled,
    required: required || children.props.required,
  });

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <Label htmlFor={id}>
        {label}
        {required && (
          <span className="ml-0.5 text-danger" aria-hidden>
            *
          </span>
        )}
      </Label>
      {control}
      {error && (
        <p id={errorId} className="text-sm text-danger">
          {error}
        </p>
      )}
      {locked && (
        <p id={lockedId} className="text-sm text-ink-muted">
          {t('permissions.lockedField', { action: locked })}
        </p>
      )}
      {help && (
        <p id={helpId} className="text-[11.5px] leading-snug text-ink-muted">
          {help}
        </p>
      )}
    </div>
  );
}
